/**
 * PATH: apps/hr/src/pages/employees/employee-detail/detail-shared-ui.tsx
 * PURPOSE: Shared UI primitives for employee detail page (tabs, cards, forms, modals)
 */

import { Loader2, X } from 'lucide-react'
import { cn } from '../../../lib/utils'

// =============================================================================
// TAB NAVIGATION
// =============================================================================

export interface Tab {
  id: string
  label: string
  icon: React.ElementType
  count?: number
}

export function TabNavigation({ tabs, activeTab, onChange }: { tabs: Tab[]; activeTab: string; onChange: (_tab: string) => void }) {
  return (
    <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
      {tabs.map((tab) => (
        <button key={tab.id} onClick={() => onChange(tab.id)}
          className={cn('flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
            activeTab === tab.id ? 'border-teal-500 text-teal-600 dark:text-teal-400' : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300')}>
          <tab.icon className="w-4 h-4" />
          {tab.label}
          {tab.count !== undefined && (
            <span className={cn('px-1.5 py-0.5 text-xs rounded-full', activeTab === tab.id ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400')}>{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// INFO CARD
// =============================================================================

export function InfoCard({ title, icon: Icon, children, action, actionLabel, className, compact }: {
  title: string; icon?: React.ElementType; children: React.ReactNode; action?: () => void; actionLabel?: string; className?: string; compact?: boolean
}) {
  return (
    <div className={cn('bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 overflow-hidden shadow-sm', className)}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-transparent">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="w-4 h-4 text-teal-600 dark:text-teal-400" />}
          <h3 className="font-semibold text-sm text-slate-900 dark:text-white">{title}</h3>
        </div>
        {action && <button onClick={action} className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors">{actionLabel || 'Edit'}</button>}
      </div>
      <div className={cn('p-5', compact && 'p-4')}>{children}</div>
    </div>
  )
}

// =============================================================================
// PROFILE SECTION & ROW (for OverviewTab)
// =============================================================================

export function ProfileSection({ title, children, onEdit }: { title: string; children: React.ReactNode; onEdit?: () => void }) {
  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 overflow-hidden">
      <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700/50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</h3>
        {onEdit && <button onClick={onEdit} className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300">Edit</button>}
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-700/50">{children}</div>
    </div>
  )
}

export function ProfileRow({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center justify-between px-5 py-3', className)}>
      <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-900 dark:text-slate-200 text-right">
        {value || <span className="text-slate-400 dark:text-slate-500">-</span>}
      </span>
    </div>
  )
}

export function StatusBadge({ status, label }: { status: 'active' | 'verified' | 'enabled' | 'subscribed' | 'inactive' | 'pending' | 'disabled'; label?: string }) {
  const styles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
    verified: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
    enabled: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
    subscribed: 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-400',
    inactive: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
    disabled: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
  }
  const labels: Record<string, string> = { active: 'Active', verified: 'Verified', enabled: 'Enabled', subscribed: 'Subscribed', inactive: 'Inactive', pending: 'Pending', disabled: 'Disabled' }
  return <span className={cn('px-2.5 py-1 text-xs font-medium rounded-full', styles[status])}>{label || labels[status]}</span>
}

// =============================================================================
// EDIT MODAL + FORM CONTROLS
// =============================================================================

export function EditModal({ isOpen, onClose, title, icon: Icon, children, onSave, isSaving }: {
  isOpen: boolean; onClose: () => void; title: string; icon?: React.ElementType; children: React.ReactNode; onSave: () => void; isSaving?: boolean
}) {
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto m-4">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5 text-teal-600 dark:text-teal-400" />}
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-4">{children}</div>
        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-800">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">Cancel</button>
          <button onClick={onSave} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

export function FormInput({ label, value, onChange, type = 'text', placeholder, required }: {
  label: string; value: string; onChange: (value: string) => void; type?: 'text' | 'email' | 'tel' | 'date'; placeholder?: string; required?: boolean
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
    </div>
  )
}

export function FormSelect({ label, value, onChange, options, placeholder, required }: {
  label: string; value: string; onChange: (value: string) => void; options: { value: string; label: string }[]; placeholder?: string; required?: boolean
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  )
}
